import * as THREE from 'three';

/**
 * Procedural brick / pixel F-22 Raptor — Dev garage craft.
 * Stealth air-superiority greys, diamond wing (aft-swept), twin canted tails, dual nozzles.
 * Local +Z is nose-forward (same as other player craft).
 */
const GREY = 0xb0b6be;
const GREY_MID = 0x8a9199;
const GREY_DARK = 0x5a6169;
const PANEL = 0x6a717c;
const EDGE = 0xc5c9d0;
const BLACK = 0x1a1e24;
const BLACK_SOFT = 0x2c323a;
const CANOPY = 0x1a2838;
const CANOPY_LIT = 0x2a4060;
const INTAKE = 0x0e1014;
const AFTERBURN = 0xff8f00;
const AFTERBURN_HOT = 0xfff59d;

/** Overall model scale vs original draft (bigger in garage + flight). */
const MODEL_SCALE = 1.48;

type Mats = {
  grey: THREE.MeshBasicMaterial;
  greyMid: THREE.MeshBasicMaterial;
  greyDark: THREE.MeshBasicMaterial;
  panel: THREE.MeshBasicMaterial;
  edge: THREE.MeshBasicMaterial;
  black: THREE.MeshBasicMaterial;
  blackSoft: THREE.MeshBasicMaterial;
  canopy: THREE.MeshBasicMaterial;
  canopyLit: THREE.MeshBasicMaterial;
  intake: THREE.MeshBasicMaterial;
  afterburn: THREE.MeshBasicMaterial;
  afterburnHot: THREE.MeshBasicMaterial;
};

function mats(): Mats {
  return {
    grey: new THREE.MeshBasicMaterial({ color: GREY }),
    greyMid: new THREE.MeshBasicMaterial({ color: GREY_MID }),
    greyDark: new THREE.MeshBasicMaterial({ color: GREY_DARK }),
    panel: new THREE.MeshBasicMaterial({ color: PANEL }),
    edge: new THREE.MeshBasicMaterial({ color: EDGE }),
    black: new THREE.MeshBasicMaterial({ color: BLACK }),
    blackSoft: new THREE.MeshBasicMaterial({ color: BLACK_SOFT }),
    canopy: new THREE.MeshBasicMaterial({ color: CANOPY }),
    canopyLit: new THREE.MeshBasicMaterial({ color: CANOPY_LIT }),
    intake: new THREE.MeshBasicMaterial({ color: INTAKE }),
    afterburn: new THREE.MeshBasicMaterial({ color: AFTERBURN, transparent: true, opacity: 0.9 }),
    afterburnHot: new THREE.MeshBasicMaterial({
      color: AFTERBURN_HOT,
      transparent: true,
      opacity: 0.95,
    }),
  };
}

/** Brick with Euler order XYZ — rotX / rotY / rotZ match Three.js. */
function brick(
  parent: THREE.Object3D,
  w: number,
  h: number,
  d: number,
  mat: THREE.Material,
  x: number,
  y: number,
  z: number,
  rotX = 0,
  rotY = 0,
  rotZ = 0,
) {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  mesh.position.set(x, y, z);
  mesh.rotation.set(rotX, rotY, rotZ);
  parent.add(mesh);
  return mesh;
}

function triWedge(
  parent: THREE.Object3D,
  radius: number,
  length: number,
  mat: THREE.Material,
  x: number,
  y: number,
  z: number,
  rotX = 0,
  rotY = 0,
  rotZ = 0,
) {
  const mesh = new THREE.Mesh(new THREE.ConeGeometry(radius, length, 3), mat);
  mesh.position.set(x, y, z);
  mesh.rotation.x = Math.PI / 2 + rotX;
  mesh.rotation.y = rotY;
  mesh.rotation.z = rotZ;
  parent.add(mesh);
  return mesh;
}

/**
 * F-22 Raptor brick fighter for the Dev spaceship garage.
 * Wings: wide at the tail, tapering forward (aft-swept diamond), not reversed.
 */
export function createF22Raptor(): THREE.Group {
  const ship = new THREE.Group();
  ship.name = 'arcade-f22-raptor';
  const m = mats();

  // ——— Fuselage / chine (nose = +Z) ———
  // Slightly longer body so empennage sits clear of the main-wing TE
  brick(ship, 0.78, 0.4, 3.15, m.greyMid, 0, 0.02, -0.15);
  brick(ship, 0.62, 0.34, 0.75, m.grey, 0, 0.04, 1.4);
  brick(ship, 0.44, 0.26, 0.52, m.panel, 0, 0.02, 1.95);
  brick(ship, 0.3, 0.2, 0.45, m.greyDark, 0, 0.0, 2.4);
  triWedge(ship, 0.17, 0.45, m.blackSoft, 0, 0.0, 2.7);
  triWedge(ship, 0.11, 0.26, m.black, 0, 0.02, 2.55, 0.06);
  // Aft boom between wing TE (~-1.35) and stabs (~-2.2)
  brick(ship, 0.7, 0.36, 0.85, m.panel, 0, 0.0, -1.75);
  brick(ship, 0.62, 0.32, 0.45, m.greyDark, 0, -0.02, -2.25);

  // Side chines
  brick(ship, 0.2, 0.13, 1.75, m.edge, 0.46, -0.02, 0.3);
  brick(ship, 0.2, 0.13, 1.75, m.edge, -0.46, -0.02, 0.3);
  brick(ship, 0.14, 0.11, 0.75, m.panel, 0.42, -0.04, 1.45);
  brick(ship, 0.14, 0.11, 0.75, m.panel, -0.42, -0.04, 1.45);

  // ——— Canopy ———
  brick(ship, 0.46, 0.3, 0.9, m.canopy, 0, 0.36, 0.5);
  brick(ship, 0.34, 0.17, 0.48, m.canopyLit, 0, 0.48, 0.65);
  brick(ship, 0.24, 0.11, 0.3, m.black, 0, 0.54, 0.92);
  triWedge(ship, 0.13, 0.3, m.canopy, 0, 0.44, 1.08, -0.25);
  brick(ship, 0.07, 0.09, 0.95, m.edge, 0.22, 0.4, 0.5);
  brick(ship, 0.07, 0.09, 0.95, m.edge, -0.22, 0.4, 0.5);

  // ——— Main wing: AFT-SWEPT diamond ———
  // Planform: tips trail toward the tail (−Z); root LE sits farther forward (+Z).
  //
  //   nose (+Z)
  //      \_____/
  //     /       \     ← mid LE
  //    /         \
  //   /___________\  ← wide trailing edge near engines (−Z)
  //
  // Root / center body of wing (mid-fuselage)
  brick(ship, 2.0, 0.09, 0.85, m.grey, 0, -0.05, 0.15);
  // Broad trailing region (rear, widest span)
  brick(ship, 3.9, 0.085, 0.95, m.greyMid, 0, -0.07, -0.55);
  brick(ship, 3.2, 0.08, 0.45, m.panel, 0, -0.06, -1.05);
  // Trailing-edge flaperon strip (furthest aft)
  brick(ship, 2.6, 0.07, 0.32, m.greyDark, 0, -0.08, -1.35);

  // Right wing (+X): panels step outboard AND aft (negative Z as |x| grows)
  // Leading-edge ramp (root → tip goes aft)
  brick(ship, 1.1, 0.075, 0.7, m.greyMid, 1.35, -0.05, 0.05, 0, 0.42, 0);
  brick(ship, 1.0, 0.07, 0.55, m.panel, 1.85, -0.04, -0.35, 0, 0.48, 0);
  brick(ship, 0.75, 0.065, 0.42, m.greyDark, 2.2, -0.03, -0.75, 0, 0.52, 0);
  // Outer tip (most aft of the wing tips)
  brick(ship, 0.5, 0.06, 0.35, m.blackSoft, 2.4, -0.02, -1.05, 0, 0.55, 0);
  triWedge(ship, 0.16, 0.32, m.black, 2.5, 0.0, -1.2, 0, 0.35, 0);

  // Left wing (−X): mirror yaw
  brick(ship, 1.1, 0.075, 0.7, m.greyMid, -1.35, -0.05, 0.05, 0, -0.42, 0);
  brick(ship, 1.0, 0.07, 0.55, m.panel, -1.85, -0.04, -0.35, 0, -0.48, 0);
  brick(ship, 0.75, 0.065, 0.42, m.greyDark, -2.2, -0.03, -0.75, 0, -0.52, 0);
  brick(ship, 0.5, 0.06, 0.35, m.blackSoft, -2.4, -0.02, -1.05, 0, -0.55, 0);
  triWedge(ship, 0.16, 0.32, m.black, -2.5, 0.0, -1.2, 0, -0.35, 0);

  // LERX / strakes — run nearly parallel to the nose (+Z), only a slight blend into the wing
  // (was heavily yawed outward and looked like open “fins” next to the nose)
  brick(ship, 0.55, 0.06, 0.95, m.greyDark, 0.62, -0.02, 1.15, 0, 0.08, 0);
  brick(ship, 0.55, 0.06, 0.95, m.greyDark, -0.62, -0.02, 1.15, 0, -0.08, 0);
  brick(ship, 0.42, 0.055, 0.55, m.blackSoft, 0.58, 0.0, 1.65, 0, 0.06, 0);
  brick(ship, 0.42, 0.055, 0.55, m.blackSoft, -0.58, 0.0, 1.65, 0, -0.06, 0);
  // Soft blend into main wing root (still almost parallel)
  brick(ship, 0.7, 0.06, 0.4, m.panel, 0.85, -0.03, 0.65, 0, 0.12, 0);
  brick(ship, 0.7, 0.06, 0.4, m.panel, -0.85, -0.03, 0.65, 0, -0.12, 0);

  // ——— Cheek intakes — long axis parallel to fuselage (+Z), not splayed ———
  brick(ship, 0.4, 0.3, 1.15, m.blackSoft, 0.52, -0.12, 0.55);
  brick(ship, 0.4, 0.3, 1.15, m.blackSoft, -0.52, -0.12, 0.55);
  brick(ship, 0.32, 0.24, 0.7, m.intake, 0.52, -0.14, 1.15);
  brick(ship, 0.32, 0.24, 0.7, m.intake, -0.52, -0.14, 1.15);
  // Forward lips (face forward, flush with body)
  brick(ship, 0.36, 0.09, 0.12, m.edge, 0.52, 0.0, 1.55);
  brick(ship, 0.36, 0.09, 0.12, m.edge, -0.52, 0.0, 1.55);
  brick(ship, 0.08, 0.24, 0.12, m.edge, 0.68, -0.12, 1.55);
  brick(ship, 0.08, 0.24, 0.12, m.edge, -0.68, -0.12, 1.55);

  // ——— Twin engines + 2D nozzles (further aft — clears wing TE at ~-1.35) ———
  for (const sx of [1, -1] as const) {
    const x = sx * 0.46;
    brick(ship, 0.52, 0.4, 1.25, m.greyDark, x, -0.08, -1.85);
    brick(ship, 0.44, 0.34, 0.38, m.panel, x, -0.08, -2.5);
    brick(ship, 0.4, 0.3, 0.2, m.black, x, -0.08, -2.78);
    brick(ship, 0.34, 0.24, 0.14, m.blackSoft, x, -0.08, -2.92);
    brick(ship, 0.28, 0.17, 0.15, m.afterburn, x, -0.08, -3.05);
    brick(ship, 0.17, 0.11, 0.13, m.afterburnHot, x, -0.08, -3.18);
  }

  // ——— Twin canted vertical tails (aft of wing, over empennage gap) ———
  // F-22: tops lean OUTBOARD. rotZ = −sx*cant (right fin tips outward toward +X).
  const cant = 0.42;
  for (const sx of [1, -1] as const) {
    const x = sx * 0.58;
    const lean = -sx * cant;
    brick(ship, 0.09, 0.8, 0.68, m.greyMid, x, 0.45, -1.85, 0, 0, lean);
    brick(ship, 0.08, 0.52, 0.45, m.panel, x + sx * 0.06, 0.7, -1.7, 0, 0, lean);
    brick(ship, 0.07, 0.32, 0.3, m.greyDark, x + sx * 0.1, 0.92, -1.58, 0, 0, lean);
    triWedge(ship, 0.13, 0.3, m.edge, x + sx * 0.12, 0.88, -1.4, -0.35, sx * 0.08, lean);
  }

  // Horizontal stabs — LE sits behind main-wing TE with a clear gap
  // Right (+X): negative yaw so LE angles outboard; left is mirrored.
  brick(ship, 1.45, 0.065, 0.45, m.grey, 1.05, 0.02, -2.25, 0, -0.28, 0);
  brick(ship, 1.45, 0.065, 0.45, m.grey, -1.05, 0.02, -2.25, 0, 0.28, 0);
  brick(ship, 0.8, 0.06, 0.32, m.panel, 1.45, 0.04, -2.45, 0, -0.34, 0);
  brick(ship, 0.8, 0.06, 0.32, m.panel, -1.45, 0.04, -2.45, 0, 0.34, 0);
  // Outer tip wedges (point outboard-aft)
  triWedge(ship, 0.12, 0.28, m.greyDark, 1.85, 0.03, -2.6, 0, -0.4, 0);
  triWedge(ship, 0.12, 0.28, m.greyDark, -1.85, 0.03, -2.6, 0, 0.4, 0);

  // Belly bay
  brick(ship, 0.6, 0.11, 1.3, m.blackSoft, 0, -0.3, 0.15);
  brick(ship, 0.42, 0.09, 0.58, m.intake, 0, -0.32, 0.95);

  // Pitot
  brick(ship, 0.07, 0.07, 0.2, m.edge, 0, 0.08, 2.65);

  ship.scale.setScalar(MODEL_SCALE);
  // Slightly larger radius for the longer empennage
  ship.userData.radius = 2.55 * MODEL_SCALE;
  return ship;
}
